import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, CircleCheck, TriangleAlert } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const band = (s) => (s >= 80 ? "text-emerald-600" : s >= 60 ? "text-amber-600" : "text-crimson-600");

export const PersonalPerformance = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/performance/me")
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, []);

  if (!data || data.score === null || data.score === undefined) return null;

  const diff = data.prev_score === null || data.prev_score === undefined
    ? null
    : Math.round((data.score - data.prev_score) * 10) / 10;

  return (
    <Card className="mb-8 border-brand-line bg-brand-tint/60 shadow-none" data-testid="personal-performance-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">My performance — {data.month}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-end gap-8">
          <div>
            <p className={`font-display text-4xl font-semibold ${band(data.score)}`} data-testid="my-score">
              {data.score}
              <span className="text-lg text-slate-400">/100</span>
            </p>
            {diff !== null && (
              <p
                className={`mt-1 flex items-center gap-1 text-xs font-semibold ${
                  diff >= 0 ? "text-emerald-600" : "text-crimson-600"
                }`}
                data-testid="my-trend"
              >
                {diff >= 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                {Math.abs(diff)} vs last month
              </p>
            )}
          </div>
          {(data.metrics || []).map((m) => (
            <div key={m.label}>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {m.label}
              </p>
              <p className="mt-0.5 text-lg font-semibold text-brand-ink">{m.value}</p>
            </div>
          ))}
        </div>

        {data.progress && !(data.progress.target > 0) && (
          <div className="mt-5" data-testid="my-progress-no-target">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {data.progress.label}
            </p>
            <p className="mt-0.5 text-sm text-slate-600">
              {data.progress.value_label || "No target set for this month"}
            </p>
          </div>
        )}

        {data.progress && data.progress.target > 0 && (
          <div className="mt-5" data-testid="my-progress">
            <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {data.progress.label}
              </p>
              <p className="text-sm font-semibold text-brand-ink">
                {data.progress.percent}% achieved
              </p>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-white">
              <div
                className="h-full rounded-full bg-crimson-500 transition-[width] duration-500"
                style={{ width: `${Math.min(100, data.progress.percent || 0)}%` }}
              />
            </div>
          </div>
        )}

        {(data.actions || []).length > 0 && (
          <div className="mt-5">
            <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-crimson-600">
              <TriangleAlert className="h-4 w-4" /> Needs your attention
            </p>
            <div className="space-y-1.5" data-testid="my-actions">
              {data.actions.slice(0, 6).map((a, i) => (
                <div
                  key={i}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-crimson-200 bg-white px-3 py-2 text-sm"
                >
                  <span className="font-medium text-slate-800">
                    {a.customer_name || a.studio_name}
                    {a.stage ? ` · ${a.stage}` : ""}
                    {a.studio_stage ? ` · studio moved to ${a.studio_stage}` : ""}
                  </span>
                  <span className="text-xs text-slate-500">
                    {a.previous !== undefined
                      ? `tier ${a.previous} → ${a.compliance}`
                      : a.days_overdue
                      ? `${a.days_overdue} days overdue`
                      : a.days
                        ? `${a.days} days since last update`
                        : a.compliance !== undefined
                          ? `${a.compliance}% compliance`
                          : ""}
                    {a.customer_id && (
                      <Link
                        to={`/customers/${a.customer_id}`}
                        className="ml-2 font-semibold text-crimson-600 hover:underline"
                      >
                        open
                      </Link>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(data.pending_quotes || []).length > 0 && (
          <p className="mt-4 text-sm text-amber-700" data-testid="my-pending-quotes">
            {data.pending_quotes.length} quote(s) awaiting a response for more than 3 days.
          </p>
        )}

        {(data.expiring || []).length > 0 && (
          <div className="mt-4" data-testid="my-expiring-warranties">
            <p className="mb-1.5 text-sm font-semibold text-slate-700">
              Warranties expiring in 30 days
            </p>
            <div className="flex flex-wrap gap-2">
              {data.expiring.slice(0, 8).map((w) => (
                <Badge key={w.certificate_no} className="border-0 bg-white text-slate-700">
                  {w.customer_name} · {w.expiry_date}
                </Badge>
              ))}
            </div>
          </div>
        )}

      </CardContent>
    </Card>
  );
};
